import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoreHorizontal, Pencil, Trash2, Workflow } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import { Badge } from "@/app/admin/_common/ui/badge";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import CuoConfirmDialog from "@/app/admin/_common/CuoConfirmDialog";
import { formatUtcDate } from "@/app/_utils/dayjs";
import { cn } from "@/app/_utils/cn";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";
import type { Automation } from "@/app/_services/AutomationService";

import { useSchemaLabels } from "./useAutomationSchema";
import {
  MODE_BADGE_CLASSES,
  MODE_DOT_CLASSES,
  MODE_LABEL,
  STATUS_BADGE_CLASSES,
  STATUS_DOT_CLASSES,
  STATUS_LABEL,
  toAutomationMode,
  toAutomationStatus,
} from "./automationStatus";

function StatusPill({ status }: { status: string }) {
  const key = toAutomationStatus(status);
  return (
    <Badge
      variant="outline"
      className={cn("gap-1.5 rounded-[7px] font-normal", STATUS_BADGE_CLASSES[key])}
    >
      <span className={cn("size-1.5 flex-none rounded-full", STATUS_DOT_CLASSES[key])} />
      {STATUS_LABEL[key]}
    </Badge>
  );
}

function ModePill({ mode }: { mode: string }) {
  const key = toAutomationMode(mode);
  return (
    <Badge
      variant="outline"
      className={cn("gap-1.5 rounded-[7px] font-normal", MODE_BADGE_CLASSES[key])}
    >
      <span className={cn("size-1.5 flex-none rounded-full", MODE_DOT_CLASSES[key])} />
      {MODE_LABEL[key]}
    </Badge>
  );
}

export default function AutomationList({
  automations,
  onDelete,
}: {
  automations: Automation[];
  onDelete: (automation: Automation) => void;
}) {
  const [pendingDelete, setPendingDelete] = useState<Automation | null>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { eventLabel, actionLabel } = useSchemaLabels();

  const open = (automation: Automation) =>
    navigate(`/admin/tools/automations/${automation.id}`);

  const actionsMenu = (automation: Automation) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="ml-auto flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
        >
          <MoreHorizontal className="size-4 shrink-0" size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => open(automation)}>
          <Pencil className="mr-2 size-4" />
          Edit
        </DropdownMenuItem>
        {/* System automations stand in for code paths that were removed, so the server
            refuses to delete them. Hiding the item avoids offering an action that
            always fails. */}
        {!automation.is_system && (
          <DropdownMenuItem
            className="text-red-600 focus:bg-red-50 focus:text-red-700"
            onClick={() => setPendingDelete(automation)}
          >
            <Trash2 className="mr-2 size-4" />
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const confirmDialog = (
    <CuoConfirmDialog
      open={pendingDelete !== null}
      onClose={() => setPendingDelete(null)}
      title="Delete automation"
      message={
        "If this automation has ever run, it will be archived instead of deleted so its " +
        "history is kept. Are you sure?"
      }
      onConfirm={async () => {
        if (pendingDelete) await onDelete(pendingDelete);
      }}
      icon={<Trash2 />}
    />
  );

  if (isMobile) {
    return (
      <div className="flex flex-col bg-card">
        {automations.length === 0 ? (
          <div className="flex min-h-[400px] flex-col">
            <CuoEmpty icon={Workflow} text="No automations" />
          </div>
        ) : (
          <div className="flex flex-col">
            {automations.map((automation) => (
              <div
                key={automation.id}
                className="flex cursor-pointer items-center gap-2.5 border-b border-border px-4 py-2.5 last:border-0"
                onClick={() => open(automation)}
              >
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="truncate text-[14px] font-medium text-foreground">
                    {automation.name}
                  </div>
                  <div className="truncate text-[12.5px] text-muted-foreground">
                    {eventLabel(automation.event_key)}
                  </div>
                </div>
                <div className="flex flex-none items-center gap-1.5">
                  <ModePill mode={automation.mode} />
                  <StatusPill status={automation.status} />
                </div>
                <div onClick={(e) => e.stopPropagation()}>{actionsMenu(automation)}</div>
              </div>
            ))}
          </div>
        )}
        {confirmDialog}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <Table
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        className={cn("min-w-[900px] table-fixed", automations.length === 0 && "h-full")}
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className="h-8 w-[22%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Name
            </TableHead>
            <TableHead className="h-8 w-[18%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Trigger
            </TableHead>
            <TableHead className="h-8 w-[16%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Action
            </TableHead>
            <TableHead className="h-8 w-[11%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Status
            </TableHead>
            <TableHead className="h-8 w-[10%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Mode
            </TableHead>
            <TableHead className="h-8 w-16 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Runs
            </TableHead>
            <TableHead className="h-8 w-[14%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Last run
            </TableHead>
            <TableHead className="h-8 w-12 pr-6 text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {automations.length === 0 ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={8} className="h-full p-0">
                <CuoEmpty icon={Workflow} text="No automations" />
              </TableCell>
            </TableRow>
          ) : (
            automations.map((automation) => (
              <TableRow key={automation.id}>
                <TableCell className="py-1.5">
                  <span
                    className="cursor-pointer truncate text-[13px] text-foreground hover:underline"
                    onClick={() => open(automation)}
                  >
                    {automation.name}
                  </span>
                </TableCell>
                <TableCell className="truncate py-1.5 text-[12.5px] text-muted-foreground">
                  {eventLabel(automation.event_key)}
                </TableCell>
                <TableCell className="truncate py-1.5 text-[12.5px] text-muted-foreground">
                  {actionLabel(automation.action_type)}
                </TableCell>
                <TableCell className="py-1.5">
                  <StatusPill status={automation.status} />
                </TableCell>
                <TableCell className="py-1.5">
                  <ModePill mode={automation.mode} />
                </TableCell>
                <TableCell className="whitespace-nowrap py-1.5 text-right text-[12.5px] text-muted-foreground">
                  {automation.run_count || "-"}
                </TableCell>
                <TableCell className="whitespace-nowrap py-1.5 text-[12.5px] text-muted-foreground">
                  {automation.last_run_at ? formatUtcDate(automation.last_run_at) : "-"}
                </TableCell>
                <TableCell className="py-1.5 pr-6 text-right">
                  {actionsMenu(automation)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {confirmDialog}
    </div>
  );
}
